import { useListBlogPosts, getListBlogPostsQueryKey } from '@workspace/api-client-react';
import { BlogEntry } from '@/components/blog/BlogEntry';
import { usePageMeta } from '@/hooks/use-page-meta';

const meshBase = `${import.meta.env.BASE_URL}meshes/`;

export function BlogMeshes() {
  return (
    <div className="blog-mesh-layer" aria-hidden="true">
      <img src={`${meshBase}satellite.svg`} alt="" className="blog-mesh blog-mesh--a" />
      <img src={`${meshBase}gear.svg`} alt="" className="blog-mesh blog-mesh--b" />
      <img src={`${meshBase}rocket.svg`} alt="" className="blog-mesh blog-mesh--c" />
    </div>
  );
}

export default function Blog() {
  usePageMeta(
    "Founder's Blog | Plasmics",
    'A dated engineering log from the founder of Plasmics, newest entry first.',
  );
  const { data, isLoading, isError, refetch, isFetching } = useListBlogPosts({
    query: { queryKey: getListBlogPostsQueryKey(), refetchInterval: 30_000, refetchOnWindowFocus: true },
  });
  const posts = data?.posts ?? [];

  return (
    <main className="relative overflow-hidden min-h-[100dvh] pt-36 md:pt-44 pb-32">
      <BlogMeshes />
      <div className="relative z-10 mx-auto w-full max-w-[38rem] px-6">
        <header className="mb-20 md:mb-28">
          <h1 className="font-mono text-xs uppercase tracking-[0.3em] text-foreground" data-testid="text-blog-heading">
            Founder's Blog
          </h1>
          <div className="mt-4 h-px w-10 bg-primary" />
        </header>

        {isLoading && (
          <div className="space-y-24" aria-busy="true" aria-label="Loading entries">
            {[0, 1].map(i => (
              <div key={i} className="space-y-4">
                <div className="h-3 w-48 bg-muted animate-pulse" />
                <div className="h-6 w-3/4 bg-muted animate-pulse" />
                <div className="h-3 w-full bg-muted animate-pulse" />
                <div className="h-3 w-11/12 bg-muted animate-pulse" />
                <div className="h-3 w-2/3 bg-muted animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {isError && (
          <div role="alert" className="border-l-2 border-destructive pl-5 py-1" data-testid="status-blog-error">
            <p className="text-foreground">The log could not be loaded just now.</p>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="mt-3 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground underline underline-offset-4 disabled:opacity-50"
              data-testid="button-blog-retry"
            >
              {isFetching ? 'Retrying' : 'Try again'}
            </button>
          </div>
        )}

        {!isLoading && !isError && posts.length === 0 && (
          <p className="text-muted-foreground leading-relaxed" data-testid="status-blog-empty">
            No entries yet. The first one will appear here.
          </p>
        )}

        {posts.length > 0 && (
          <div className="blog-log">
            {posts.map(post => (
              <BlogEntry key={post.id} post={post} testId={`entry-blog-${post.id}`} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
